import { computed, ref } from 'vue';
import { api } from '@/api';
import type { AdminUserInfo, Me, Tenant } from '@/types';

/**
 * Module-level shared state — this is an admin panel with one signed-in session per tab, so a
 * store library would only add ceremony. Every page and the rail read from these same refs rather
 * than re-fetching, which is also what keeps "refresh after a mutation" a single call instead of a
 * per-component concern.
 */
export const me = ref<Me | null>(null);
export const tenants = ref<Tenant[]>([]);
export const scopes = ref<string[]>([]);
export const eventTypes = ref<string[]>([]);

/** Admins only — null while not yet loaded (or for a Member, who never needs it). Used for the Team rail count. */
export const users = ref<AdminUserInfo[] | null>(null);

export const isAdmin = computed(() => me.value?.role === 'Admin');

/** The Create tenant dialog is mounted once in AppShell; the rail "+" and the Tenants page button both just flip this. */
export const createTenantOpen = ref(false);

/** The Find tenant palette is mounted once in AppShell; the rail's Find tenant field and the global
 * ⌘K/Ctrl+K shortcut both just flip this. */
export const findTenantOpen = ref(false);

/**
 * me + tenants + scopes + event-types load in parallel, same as App.vue did before the shell had
 * a rail to feed too. Admins additionally need the users list for the Team nav count — Members
 * never see Team, so they never pay for that call.
 */
let settleFirstLoad: () => void = () => {};

/**
 * Settles once the first loadSession() has finished, whether it succeeded or not. The router's
 * Admin-only guard waits on it: the very first navigation (a deep link or reload on /team) runs
 * before /api/admin/me is back, when isAdmin is still false, and would bounce an Admin to /tenants.
 */
export const firstLoad = new Promise<void>(resolve => (settleFirstLoad = resolve));

export async function loadSession(): Promise<void> {
  try {
    [me.value, tenants.value, scopes.value, eventTypes.value] = await Promise.all([
      api<Me>('/api/admin/me'),
      api<Tenant[]>('/api/admin/tenants'),
      api<string[]>('/api/admin/scopes'),
      api<string[]>('/api/admin/event-types')
    ]);

    if (me.value.role === 'Admin') {
      await refreshUsers();
    }
  } finally {
    settleFirstLoad();
  }
}

export async function refreshTenants(): Promise<void> {
  tenants.value = await api<Tenant[]>('/api/admin/tenants');
}

/** After invite/revoke/access edits. */
export async function refreshUsers(): Promise<void> {
  users.value = await api<AdminUserInfo[]>('/api/admin/users');
}

export function findTenant(slug: string): Tenant | undefined {
  return tenants.value.find(tenant => tenant.slug === slug);
}
